import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useEmpresa } from "./EmpresaContext";
import { api } from "../api";

const AuthContext = createContext();

function normalizarPermissoes(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

  const permissoesBool = {};
  Object.keys(raw).forEach((key) => {
    permissoesBool[key] = !!raw[key];
  });

  return permissoesBool;
}

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const { salvarEmpresas, trocarEmpresa, empresaAtiva } = useEmpresa();

  const persistirUsuario = useCallback((user) => {
    localStorage.setItem("usuarioLogado", JSON.stringify(user));
  }, []);

  const atualizarPermissoes = useCallback(
    async (usuarioId, empresaId) => {
      if (!usuarioId || !empresaId) return;

      try {
        const response = await api.get(`/permissoes/${usuarioId}/${empresaId}`);
        const permissoesAtualizadas = response?.data || {};
        const permissoesBool = normalizarPermissoes(permissoesAtualizadas);

        setUsuario((prev) => {
          if (!prev) return prev;

          const novoUsuario = {
            ...prev,
            permissoes: {
              ...(prev.permissoes || {}),
              [empresaId]: permissoesBool,
            },
          };

          persistirUsuario(novoUsuario);

          if (import.meta.env.DEV) {
            console.log(
              "✅ Permissões atualizadas no contexto para empresa",
              empresaId,
              novoUsuario.permissoes[empresaId],
            );
          }

          return novoUsuario;
        });
      } catch (error) {
        console.error("❌ Erro ao atualizar permissões:", error);
      }
    },
    [persistirUsuario],
  );

  useEffect(() => {
    // Mesmo se localStorage estiver indisponível (privacidade/extensões),
    // não mantenha a aplicação presa no estado inicial de carregamento.
    try {
      const usuarioSalvo = localStorage.getItem("usuarioLogado");
      if (usuarioSalvo) {
        let user = null;
        try {
          user = JSON.parse(usuarioSalvo);
        } catch (error) {
          console.error("❌ Erro ao interpretar usuário salvo:", error);
          localStorage.removeItem("usuarioLogado");
        }

        if (user && typeof user === "object" && user.id) {
          setUsuario(user);
          if (Array.isArray(user.empresas)) {
            salvarEmpresas(user.empresas);
            let empresaInicial = null;
            try {
              const empresaRaw = localStorage.getItem("empresaAtiva");
              const empresaSalva = empresaRaw ? JSON.parse(empresaRaw) : null;
              empresaInicial = user.empresas.find(
                (emp) => String(emp.id) === String(empresaSalva?.id),
              ) || null;
            } catch (error) {
              console.warn("Empresa salva indisponível; usando empresa do login:", error);
            }
            trocarEmpresa(empresaInicial || user.empresas[0] || null);
          }
        } else if (user) {
          console.warn("Sessão salva sem usuário válido.");
        }
      }
    } catch (error) {
      console.error("❌ Não foi possível restaurar a sessão:", error);
    } finally {
      setCarregando(false);
    }
  }, [salvarEmpresas, trocarEmpresa]);

  useEffect(() => {
    if (usuario?.id && empresaAtiva?.id) {
      atualizarPermissoes(usuario.id, empresaAtiva.id);
    }
  }, [empresaAtiva?.id, usuario?.id, atualizarPermissoes]);

  const login = useCallback(
    async ({ token, usuario: usuarioLogin }) => {
      try {
        if (token) {
          localStorage.setItem("token", token);
        }

        if (!usuarioLogin) {
          console.warn("⚠️ Login sem dados de usuário");
          return;
        }

        const usuarioNormalizado = {
          ...usuarioLogin,
          permissoes: usuarioLogin?.permissoes || {},
          empresas: Array.isArray(usuarioLogin?.empresas)
            ? usuarioLogin.empresas
            : [],
        };

        setUsuario(usuarioNormalizado);
        persistirUsuario(usuarioNormalizado);

        if (import.meta.env.DEV) {
          console.log("✅ Login realizado:", usuarioNormalizado);
        }

        if (usuarioNormalizado.empresas.length > 0) {
          salvarEmpresas(usuarioNormalizado.empresas);

          const empresaSalva = localStorage.getItem("empresaAtiva");
          let empresaInicial = null;

          if (empresaSalva) {
            try {
              const empresaParse = JSON.parse(empresaSalva);
              empresaInicial = usuarioNormalizado.empresas.find(
                (emp) => String(emp.id) === String(empresaParse?.id),
              );
            } catch (_) {
              empresaInicial = null;
            }
          }

          trocarEmpresa(
            empresaInicial ||
              usuarioNormalizado.empresas.find(
                (emp) =>
                  String(emp.id) === String(usuarioNormalizado.empresa_id),
              ) ||
              usuarioNormalizado.empresas[0],
          );
        } else {
          console.warn("⚠️ Usuário logado sem empresas vinculadas no payload.");
          salvarEmpresas([]);
          trocarEmpresa(null);
        }
      } catch (error) {
        console.error("❌ Erro ao concluir login:", error);
      }
    },
    [persistirUsuario, salvarEmpresas, trocarEmpresa],
  );

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");

      if (token) {
        await api.post("/logout");
      }
    } catch (error) {
      console.error("⚠️ Erro ao marcar usuário offline:", error);
    }

    localStorage.removeItem("usuarioLogado");
    localStorage.removeItem("token");
    localStorage.removeItem("empresaAtiva");

    setUsuario(null);
    salvarEmpresas([]);
    trocarEmpresa(null);

    console.log("🚪 Logout executado com sucesso");
  }, [salvarEmpresas, trocarEmpresa]);

  return (
    <AuthContext.Provider
      value={{
        usuario,
        setUsuario,
        login,
        logout,
        carregando,
        atualizarPermissoes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
